"use client";

import { c as _c } from "react/compiler-runtime";
import { menuSeparatorClassName } from "@/components/ui/menu-surface";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// cmdk parts on the Figma menu spec: the filter header is 36px over a 1px
// divider, rows are 32px with an 8px-radius highlight, and the list keeps the
// 4px/6px menu padding. "palette" parts are the ⌘K command menu: a 48px input
// row, a taller list, 40px rows and 12px group headings.
/** Use when building a searchable command or option menu, such as a picker inside a popover. */
export function Command(t0) {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex h-full w-full flex-col overflow-hidden bg-bg-surface text-text-primary", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
Command.displayName = CommandPrimitive.displayName;

// `customAction` sits at the end of the input row, such as a spinner or close button.
export function CommandInput(t0) {
  const $ = _c(29);
  let className;
  let customAction;
  let props;
  let ref;
  let t1;
  let t2;
  let t3;
  let wrapperClassName;
  if ($[0] !== t0) {
    ({
      className,
      showSearchIcon: t1,
      customAction,
      hasBorder: t2,
      wrapperClassName,
      variant: t3,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = customAction;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = wrapperClassName;
  } else {
    className = $[1];
    customAction = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    wrapperClassName = $[8];
  }
  const showSearchIcon = t1 === undefined ? true : t1;
  const hasBorder = t2 === undefined ? true : t2;
  const variant = t3 === undefined ? "menu" : t3;
  let t4;
  if ($[9] !== hasBorder || $[10] !== variant || $[11] !== wrapperClassName) {
    t4 = cn("flex shrink-0 items-center", variant === "menu" && ["box-content h-9 grow basis-auto gap-2 border-b pl-3.5 pr-3", hasBorder ? "border-border-subtle" : "border-transparent"], variant === "palette" && "h-12 gap-2.5 px-4.5 max-md:box-content max-md:pt-safe-top", wrapperClassName);
    $[9] = hasBorder;
    $[10] = variant;
    $[11] = wrapperClassName;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== showSearchIcon || $[14] !== variant) {
    t5 = showSearchIcon && <Search aria-hidden="true" className={cn("shrink-0 text-text-tertiary", variant === "menu" ? "size-3.5" : "size-4")} />;
    $[13] = showSearchIcon;
    $[14] = variant;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const t6 = variant === "menu" && "text-base text-text-primary placeholder:text-text-tertiary";
  const t7 = variant === "palette" && "text-touch placeholder:text-text-placeholder md:text-lg [@media(pointer:coarse)]:text-touch";
  let t8;
  if ($[16] !== className || $[17] !== t6 || $[18] !== t7) {
    t8 = cn("flex h-full w-full bg-transparent outline-none disabled:cursor-not-allowed disabled:opacity-50", t6, t7, className);
    $[16] = className;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== props || $[21] !== ref || $[22] !== t8) {
    t9 = <CommandPrimitive.Input ref={ref} className={t8} {...props} />;
    $[20] = props;
    $[21] = ref;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== customAction || $[25] !== t4 || $[26] !== t5 || $[27] !== t9) {
    t10 = <div className={t4} cmdk-input-wrapper="">{t5}{t9}{customAction}</div>;
    $[24] = customAction;
    $[25] = t4;
    $[26] = t5;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
CommandInput.displayName = CommandPrimitive.Input.displayName;

// "palette" is the ⌘K list: taller, thin scrollbar, full height on mobile.
export function CommandList(t0) {
  const $ = _c(13);
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "menu" : t1;
  const t2 = variant === "menu" && "max-h-menu-list py-1";
  let t3;
  if ($[5] !== className || $[6] !== t2 || $[7] !== variant) {
    t3 = cn("overflow-y-auto overflow-x-hidden px-1.5", t2, variant === "palette" && ["max-h-palette-list py-1.5 max-md:h-full max-md:max-h-none", "[scrollbar-width:thin] [scrollbar-color:var(--color-border-subtle)_transparent]"], className);
    $[5] = className;
    $[6] = t2;
    $[7] = variant;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== ref || $[11] !== t3) {
    t4 = <CommandPrimitive.List ref={ref} className={t3} {...props} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
CommandList.displayName = CommandPrimitive.List.displayName;
export function CommandEmpty(t0) {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("px-2 pb-2 pt-1.5 text-base font-book text-text-tertiary", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive.Empty ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;
export function CommandGroup(t0) {
  const $ = _c(13);
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "menu" : t1;
  const t2 = variant === "menu" && "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-xs";
  let t3;
  if ($[5] !== className || $[6] !== t2 || $[7] !== variant) {
    t3 = cn("overflow-hidden", "[&_[cmdk-group-heading]]:flex [&_[cmdk-group-heading]]:h-7 [&_[cmdk-group-heading]]:items-center", "[&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-tertiary", t2, variant === "palette" && ["[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1", "[&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:tracking-normal"], className);
    $[5] = className;
    $[6] = t2;
    $[7] = variant;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== ref || $[11] !== t3) {
    t4 = <CommandPrimitive.Group ref={ref} className={t3} {...props} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
CommandGroup.displayName = CommandPrimitive.Group.displayName;
export function CommandSeparator(t0) {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("-mx-1.5", menuSeparatorClassName, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <CommandPrimitive.Separator ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

// "palette" rows are the ⌘K results: 40px minimum, 20px leading media.
export function CommandItem(t0) {
  const $ = _c(13);
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "menu" : t1;
  const t2 = variant === "menu" && "h-8 gap-2 px-2";
  const t3 = variant === "palette" && "min-h-10 gap-2.5 px-3 py-1.5 [&_[data-avatar]]:size-5";
  let t4;
  if ($[5] !== className || $[6] !== t2 || $[7] !== t3) {
    t4 = cn("relative flex cursor-default select-none items-center rounded-control outline-none", "text-base font-book text-text-secondary", t2, t3, "data-[state=open]:bg-fill-hover data-[state=open]:text-text-primary", "data-[selected=true]:bg-fill-hover data-[selected=true]:text-text-primary", "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50", "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-text-tertiary", "data-[selected=true]:[&_svg]:text-text-primary", className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== props || $[10] !== ref || $[11] !== t4) {
    t5 = <CommandPrimitive.Item ref={ref} className={t4} {...props} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
CommandItem.displayName = CommandPrimitive.Item.displayName;